import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Copy, MessageCircle, Phone } from "lucide-react";
import { buildTelUrl, buildWhatsappUrl } from "@/lib/whatsapp";
import { trackCtaClick } from "@/lib/analytics";
import { useSiteConfig } from "@/hooks/useSiteConfig";

type Props = {
  message?: string;
  className?: string;
  /** Analytics location tag — e.g. "contact", "final-cta", "navbar". */
  location?: string;
  /** Visual tone. `dark` swaps neutrals for on-dark surfaces. */
  tone?: "light" | "dark";
  /** Hide the Call and Copy buttons — show only the primary WhatsApp CTA. */
  compact?: boolean;
  /** Override the primary CTA label (default: "Chat on WhatsApp"). */
  chatLabel?: string;
};

/**
 * WhatsApp CTA with graceful fallback:
 * - Tries to open wa.me in a new tab
 * - If the tab never gains focus / page stays visible (network blocks
 *   api.whatsapp.com, WhatsApp not installed on mobile), surfaces an
 *   inline fallback with tel: + copy-to-clipboard.
 */
export function WhatsAppActions({
  message,
  className,
  location = "unknown",
  tone = "light",
  compact = false,
  chatLabel = "Chat on WhatsApp",
}: Props) {
  const config = useSiteConfig();
  const waUrl = buildWhatsappUrl(config.whatsappNumber, message ?? config.whatsappDefaultMessage);
  const tel = buildTelUrl(config.telNumber || config.whatsappNumber);
  const displayNumber = config.whatsappNumberDisplay || config.whatsappNumber;
  const [fallback, setFallback] = useState(false);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  const handleOpen = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      trackCtaClick({
        action: "whatsapp_chat",
        location,
        label: chatLabel,
        href: waUrl,
      });
      // Let the browser open the link normally; then detect if the user
      // never actually left this page after ~1.5s.
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        if (document.visibilityState === "visible") {
          setFallback(true);
        }
      }, 1500);

      // If the browser blocks the popup entirely, show fallback immediately.
      // (window.open returning null would signal that, but we're using an
      //  anchor, so rely on the visibility check above.)
      void e;
    },
    [chatLabel, location, waUrl],
  );

  const handleCall = useCallback(() => {
    trackCtaClick({ action: "call", location, href: tel });
  }, [location, tel]);

  const handleCopy = useCallback(async () => {
    trackCtaClick({ action: "copy_number", location });
    try {
      await navigator.clipboard.writeText(config.whatsappNumber);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers / insecure contexts
      const el = document.createElement("input");
      el.value = config.whatsappNumber;
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      } catch {
        /* noop */
      }
      document.body.removeChild(el);
    }
  }, [location, config.whatsappNumber]);

  const secondaryClass =
    tone === "dark"
      ? "inline-flex items-center gap-2 rounded-full border border-primary-foreground/40 bg-primary-foreground/5 px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-foreground/10"
      : "inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted";

  const fallbackClass =
    tone === "dark"
      ? "mt-4 rounded-2xl border border-primary-foreground/25 bg-primary-foreground/10 p-4 text-sm text-primary-foreground"
      : "mt-4 rounded-2xl border border-border bg-muted/50 p-4 text-sm text-foreground";

  const fallbackMutedClass =
    tone === "dark" ? "mt-1 text-primary-foreground/80" : "mt-1 text-muted-foreground";

  return (
    <div className={className}>
      <div className="flex flex-wrap gap-3">
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleOpen}
          aria-label={`Chat with us on WhatsApp at ${displayNumber}`}
          className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3 text-sm font-medium text-accent-foreground shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:opacity-95 motion-reduce:transform-none"
        >
          <MessageCircle className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
          {chatLabel}
        </a>
        {!compact && (
          <>
            <button
              type="button"
              onClick={handleCopy}
              aria-label={`Copy phone number ${displayNumber}`}
              aria-live="polite"
              className={secondaryClass}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" aria-hidden="true" strokeWidth={1.75} />
                  Copy number
                </>
              )}
            </button>
          </>
        )}
      </div>

      {fallback && (
        <div role="status" className={fallbackClass}>
          <p className="font-medium">Couldn't open WhatsApp?</p>
          <p className={fallbackMutedClass}>
            Some networks block WhatsApp Web. You can copy the number above and message us from the WhatsApp app.
          </p>
        </div>
      )}
    </div>
  );
}